import type { CSSProperties } from "react";
import { resolveBackgroundColor } from "./caption-background";
import { fontFamilyStackFor } from "@hightouch/lib/ad-studio/html-editor/font-family";
import type {
  CaptionBackgroundPadding,
  CaptionCornerRadii,
  CaptionStyle,
} from "./caption-style";

/**
 * Margins as fractions of the video size, clear of platform UI overlays.
 * Vertical video needs a larger bottom margin for like/comment buttons and
 * larger side margins for swipe gestures.
 */
const SAFE_ZONE = {
  vertical: { top: 0.12, bottom: 0.2, sides: 0.08 },
  landscape: { top: 0.08, bottom: 0.08, sides: 0.05 },
  verticalVideoMaxAspectRatio: 0.75,
} as const;

export interface CaptionSafeZone {
  top: number;
  bottom: number;
  sides: number;
}

/** The safe-zone margins the caption strip keeps clear, for this video size. */
export function captionSafeZone(
  videoWidth: number,
  videoHeight: number,
): CaptionSafeZone {
  return videoWidth / videoHeight < SAFE_ZONE.verticalVideoMaxAspectRatio
    ? SAFE_ZONE.vertical
    : SAFE_ZONE.landscape;
}

export const CAPTION_LINE_HEIGHT = 1.3;

/**
 * The typography of the caption text block, the box every word span sits in.
 * `scale` is screen pixels per composition pixel, so the editor can lay the
 * same text over a scaled-down canvas.
 */
export function captionTextStyle(
  style: Pick<
    CaptionStyle,
    "fontFamily" | "fontSize" | "fontWeight" | "maxWidth"
  >,
  scale = 1,
): CSSProperties {
  return {
    // Brand fonts arrive as hashed filename aliases like `6f57dd2c9c7ec1f1_s.p`.
    // A `.` is not a valid bare `<custom-ident>`, so the raw name would make
    // the browser reject the declaration and fall back to the UA serif.
    fontFamily: fontFamilyStackFor(style.fontFamily),
    fontSize: px(style.fontSize * scale),
    fontWeight: style.fontWeight,
    lineHeight: CAPTION_LINE_HEIGHT,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    ...(style.maxWidth !== undefined && {
      maxWidth: px(style.maxWidth * scale),
    }),
  };
}

/** Padding as a fraction of the font size, for captions that set none. */
const DEFAULT_PADDING_EM: CaptionBackgroundPadding = { x: 0.4, y: 0.05 };

/** The padding the background paints, in composition pixels. */
export function captionBackgroundPadding(
  style: Pick<CaptionStyle, "fontSize" | "backgroundPadding">,
): CaptionBackgroundPadding {
  return (
    style.backgroundPadding ?? {
      x: style.fontSize * DEFAULT_PADDING_EM.x,
      y: style.fontSize * DEFAULT_PADDING_EM.y,
    }
  );
}

/** The background's four corner radii, in composition pixels. */
export function captionCornerRadii(
  style: Pick<CaptionStyle, "backgroundRadius">,
): CaptionCornerRadii {
  const r = style.backgroundRadius ?? 0;
  return typeof r === "number" ? { tl: r, tr: r, br: r, bl: r } : r;
}

/**
 * The CSS for the one box behind the caption text, or nothing when the
 * caption has no background. `scale` is screen pixels per composition pixel,
 * so the editor can paint the same box over a scaled-down canvas.
 */
export function captionBackgroundStyle(
  style: Pick<
    CaptionStyle,
    | "fontSize"
    | "backgroundColor"
    | "backgroundOpacity"
    | "backgroundRadius"
    | "backgroundPadding"
  >,
  scale = 1,
): CSSProperties {
  const backgroundColor = resolveBackgroundColor(
    style.backgroundColor,
    style.backgroundOpacity,
  );
  if (!backgroundColor) return {};

  const padding = captionBackgroundPadding(style);
  const css: CSSProperties = {
    backgroundColor,
    padding: `${px(padding.y * scale)} ${px(padding.x * scale)}`,
  };
  const { tl, tr, br, bl } = captionCornerRadii(style);
  if (tl || tr || br || bl) {
    css.borderRadius = [tl, tr, br, bl].map((r) => px(r * scale)).join(" ");
  }
  return css;
}

/**
 * The CSS that decorates the glyphs themselves, an outline and a drop shadow,
 * plus the caption's overall opacity. Set on the caption's outer span: the
 * stroke and shadow inherit into every word span, animated or not, and the
 * opacity fades the text and its background as one.
 */
export function captionTextEffectsStyle(
  style: Pick<CaptionStyle, "stroke" | "shadow" | "opacity">,
  scale = 1,
): CSSProperties {
  const css: CSSProperties = {};

  if ((style.opacity ?? 1) < 1) css.opacity = style.opacity;

  if (style.stroke && style.stroke.width > 0) {
    // A CSS text stroke is centered on the glyph edge, so half of it would
    // eat into the fill. Painting the stroke under the fill hides that half,
    // and doubling the width makes `width` the thickness that stays visible.
    css.WebkitTextStroke = `${px(style.stroke.width * 2 * scale)} ${style.stroke.color}`;
    css.paintOrder = "stroke fill";
  }

  // An all-zero shadow sits exactly under the glyph and paints nothing.
  if (
    style.shadow &&
    (style.shadow.x !== 0 || style.shadow.y !== 0 || style.shadow.blur !== 0)
  ) {
    const { x, y, blur, color } = style.shadow;
    css.textShadow = `${px(x * scale)} ${px(y * scale)} ${px(blur * scale)} ${color}`;
  }

  return css;
}

/** Two decimals, so a scaled value carries no float noise into the CSS. */
function px(value: number): string {
  return `${Math.round(value * 100) / 100}px`;
}
